import React, { createContext, useContext, useEffect, useMemo, useState, ReactNode } from 'react';

// Types mirrored from former Redux slices (re-declared here to avoid importing createSlice-based files)
export interface Area {
  id: string;
  points: [number, number][];
  type: string;
  camera?: string | number;
  name?: string | null;
}

export interface Frame {
  url: string;
  width: number;
  height: number;
  /** id кадра на бэкенде — для серверного предпросмотра кропа (осветление стола). */
  id?: number;
}

export interface AreasState {
  entities: { [id: string]: Area };
  frame: Frame | null;
}

export type PolygonEntityType = 'standalone' | 'rack' | 'shelf' | 'table' | 'queue' | 'cashier' | 'staff' | 'order_waiting';

export type CheckoutZoneType = 'QUEUE' | 'CASHIER' | 'STAFF' | 'ORDER_WAITING';

export interface CheckoutZoneData {
  id: string;
  group: string;
  name: string;
  type: CheckoutZoneType;
  operations_dashboard_id?: string | null;
  areas: string[];           // PolygonArea ids resolved from group
  queue?: { id: string; max_allowed_size: number } | null;
  staffZone?: { id: string; max_workers: number } | null;
}

export interface Rack {
  id: string;
  area: string;
  shelves: string[];
  name?: string | null;
  rack_type?: string | null;
  perspective_width?: number | null;
  perspective_height?: number | null;
  initial_width?: number | null;
  initial_height?: number | null;
  is_composite?: boolean;
  predict_percentage?: number;
  /** Стеллаж с одной полкой: полка едет за стеллажом при правке (#1547/#1549). */
  single_shelf?: boolean;
  merchandising_dashboard_id?: string | null;
  /** Архив стеллажа: скрыт из анализа, разметка/история сохранены, обратимо. */
  is_archived?: boolean;
}

export interface RacksState {
  entities: { [id: string]: Rack };
}

export interface Shelf {
  id: string;
  area: string;
  rack: string;
  name?: string | null;
  bottom_points_indices?: number[] | null;
  top_points_indices?: number[] | null;
  perspective_width?: number | null;
  perspective_height?: number | null;
  empty_shelf_score?: number | null;
  shelf_num_on_rack?: number | null;
  max_effective_percentage?: number | null;
  /** null = как у стеллажа (AI197). */
  predict_percentage?: boolean | null;
  /** Тип продукции полки поверх стеллажа; null — как у стеллажа (#3288). */
  rack_type?: string | null;
}

export interface ShelvesState {
  entities: { [id: string]: Shelf };
}

export interface Table {
  id: string;
  area: string;
  name?: string | null;
  perspective_width?: number | null;
  perspective_height?: number | null;
  /** False for fixed surfaces (bar counters): the pipeline then skips the
   *  "is it in its usual place" check, so they never show «стол не на месте». */
  can_be_physically_moved?: boolean;
  /** «Кадр испорчен» — только засвет: в полигоне по разметке всегда несколько столешниц. */
  spoiled_glare_only?: boolean;
  /** Осветление кропа стола, 0..1 (0 — выкл). */
  crop_brighten?: number;
  /** Архив стола: скрыт из анализа, разметка/история сохранены, обратимо. */
  is_archived?: boolean;
}

export interface TablesState {
  entities: { [id: string]: Table };
}

export type CanvasMode =
  | 'view'
  | 'create_standalone' | 'create_rack' | 'create_shelf' | 'create_table'
  | 'create_queue' | 'create_cashier' | 'create_staff' | 'create_order_waiting';

export interface UIState {
  mode: CanvasMode;
  selectedPolygon: string | null;
  selectedPolygonType: PolygonEntityType | null;
}

export interface CameraMeta {
  id: string;
  name: string;
  active: boolean;
  online: boolean;
}

export interface CameraState {
  selectedCameraId: string | null;
  selectedCamera: CameraMeta | null;
}

export interface ZoneState {
  selectedZoneId: string | null;
  /** «Все филиалы» mode: the dashboard/report render a portfolio of every
   *  accessible branch. selectedZoneId stays null so every single-branch
   *  surface (management pages, canvas, BranchRequiredState gates) degrades
   *  exactly as if no branch were picked. URL contract: ?zone=all. */
  allBranches: boolean;
  /** Группа филиалов (Алексей 22.10, #1969): режим «Все филиалы», но только по филиалам
   *  группы. allBranches при этом true — все портфельные экраны работают как обычно и
   *  сужаются через useBranchScope. URL contract: ?zone=group:<id>. */
  branchGroupId: string | null;
}

/** The ?zone= URL literal for the all-branches mode. */
export const ALL_BRANCHES_PARAM = 'all';
/** ?zone=group:<id> — режим группы филиалов. */
// «group-2», а не «group:2» — двоеточие в адресе превращалось в %3A (Алексей 25.10 #2323).
// Старые ссылки с «group:» по-прежнему открываются (parseGroupParam).
export const GROUP_PARAM_PREFIX = 'group-';
const LEGACY_GROUP_PREFIX = 'group:';
/** id группы из ?zone=group-N (или старого group:N); иначе null. */
export function parseGroupParam(v: string | null | undefined): string | null {
  if (!v) return null;
  if (v.startsWith(GROUP_PARAM_PREFIX)) return v.slice(GROUP_PARAM_PREFIX.length);
  if (v.startsWith(LEGACY_GROUP_PREFIX)) return v.slice(LEGACY_GROUP_PREFIX.length);
  return null;
}

export interface CheckoutZonesState {
  entities: { [id: string]: CheckoutZoneData };
}

export interface PolygonsState {
  areas: AreasState;
  racks: RacksState;
  shelves: ShelvesState;
  tables: TablesState;
  checkoutZones: CheckoutZonesState;
  ui: UIState;
}

/** Zone-wide summary: all entities across all cameras in the branch */
export interface ZoneSummary {
  areas: Area[];
  racks: Rack[];
  shelves: Shelf[];
  tables: Table[];
  checkoutZones: CheckoutZoneData[];
  /** Map areaId → cameraId for camera-jump */
  areaCameraMap: Record<string, string>;
}

const emptyZoneSummary: ZoneSummary = {
  areas: [], racks: [], shelves: [], tables: [], checkoutZones: [], areaCameraMap: {},
};

export interface PendingEdit {
  entityId: string;
  entityType: PolygonEntityType;
  /** If true, the consumer should open the entity's edit-details dialog
   *  (the same one as the toolbar's "Редактировать" button) right after
   *  startEditing. Used by the sidebar's three-dot Edit action. */
  openDetails?: boolean;
}

export interface AppState {
  camera: CameraState;
  zone: ZoneState;
  polygons: PolygonsState;
  frameLoading: boolean;
  zoneSummary: ZoneSummary;
  zoneSummaryLoading: boolean;
  pendingEdit: PendingEdit | null;
}

const initialState: AppState = {
  camera: { selectedCameraId: null, selectedCamera: null },
  zone: { selectedZoneId: null, allBranches: false, branchGroupId: null },
  frameLoading: false,
  zoneSummary: emptyZoneSummary,
  zoneSummaryLoading: false,
  pendingEdit: null,
  polygons: {
    areas: { entities: {}, frame: null },
    racks: { entities: {} },
    shelves: { entities: {} },
    tables: { entities: {} },
    checkoutZones: { entities: {} },
    ui: { mode: 'view', selectedPolygon: null, selectedPolygonType: null },
  },
};

export interface AppActions {
  setCamera: (id: string) => void;
  setSelectedCamera: (camera: CameraMeta | null) => void;
  setZone: (id: string) => void;
  setAllBranches: () => void;
  setBranchGroup: (id: string) => void;
  setFrame: (frame: Frame | null) => void;
  setAreas: (areas: Area[]) => void;
  setRacks: (racks: Rack[]) => void;
  setShelves: (shelves: Shelf[]) => void;
  setTables: (tables: Table[]) => void;
  setCheckoutZones: (zones: CheckoutZoneData[]) => void;
  setMode: (mode: CanvasMode) => void;
  setSelectedPolygon: (polygonId: string | null, type: PolygonEntityType | null) => void;
  setFrameLoading: (loading: boolean) => void;
  setZoneSummary: (summary: ZoneSummary) => void;
  setZoneSummaryLoading: (loading: boolean) => void;
  requestEdit: (entityId: string, entityType: PolygonEntityType, options?: { openDetails?: boolean }) => void;
  clearPendingEdit: () => void;
}

const StateContext = createContext<AppState | undefined>(undefined);
const ActionsContext = createContext<AppActions | undefined>(undefined);

export const StoreProvider = ({ children }: { children: ReactNode }) => {
  const [state, setState] = useState<AppState>(() => {
    const params = new URLSearchParams(window.location.search);
    const urlZoneId = params.get('zone');
    const urlCameraId = params.get('camera');
    let s = initialState;
    if (urlZoneId === ALL_BRANCHES_PARAM) s = { ...s, zone: { selectedZoneId: null, allBranches: true, branchGroupId: null } };
    else if (parseGroupParam(urlZoneId)) s = { ...s, zone: { selectedZoneId: null, allBranches: true, branchGroupId: parseGroupParam(urlZoneId) } };
    else if (urlZoneId) s = { ...s, zone: { selectedZoneId: urlZoneId, allBranches: false, branchGroupId: null } };
    if (urlCameraId) s = { ...s, camera: { ...s.camera, selectedCameraId: urlCameraId } };
    // ?edit=<тип>:<id> — открыть форму полигона (калибровка открывает зонирование в новой вкладке,
    // Эдем 01.11 #8367). Канвас дождётся кадра и полигонов камеры, как после requestEdit.
    const [editType, editId] = (params.get('edit') ?? '').split(':');
    if (urlCameraId && editType && editId) {
      s = { ...s, pendingEdit: { entityId: editId, entityType: editType as PolygonEntityType, openDetails: true } };
    }
    return s;
  });
  // Параметр одноразовый: без этого обновление вкладки снова открывало бы форму.
  useEffect(() => {
    const url = new URL(window.location.href);
    if (!url.searchParams.has('edit')) return;
    url.searchParams.delete('edit');
    window.history.replaceState(window.history.state, '', url);
  }, []);

  const actions = useMemo<AppActions>(() => ({
    setCamera: (id: string) => setState(prev => ({
      ...prev,
      camera: {
        selectedCameraId: id || null,
        selectedCamera: id ? prev.camera.selectedCamera : null,
      }
    })),
    setSelectedCamera: (camera: CameraMeta | null) => setState(prev => ({
      ...prev,
      camera: { ...prev.camera, selectedCamera: camera },
    })),
    setZone: (id: string) => setState(prev => ({ ...prev, zone: { selectedZoneId: id, allBranches: false, branchGroupId: null } })),
    setAllBranches: () => setState(prev => ({ ...prev, zone: { selectedZoneId: null, allBranches: true, branchGroupId: null } })),
    setBranchGroup: (id: string) => setState(prev => ({ ...prev, zone: { selectedZoneId: null, allBranches: true, branchGroupId: id } })),
    setFrame: (frame: Frame | null) => setState(prev => ({
      ...prev,
      polygons: { ...prev.polygons, areas: { ...prev.polygons.areas, frame } },
    })),
    setAreas: (areas: Area[]) => setState(prev => ({
      ...prev,
      polygons: {
        ...prev.polygons,
        areas: {
          ...prev.polygons.areas,
          entities: areas.reduce<Record<string, Area>>((acc, a) => { acc[a.id] = a; return acc; }, {}),
        },
      },
    })),
    setRacks: (racks: Rack[]) => setState(prev => ({
      ...prev,
      polygons: {
        ...prev.polygons,
        racks: {
          entities: racks.reduce<Record<string, Rack>>((acc, r) => { acc[r.id] = r; return acc; }, {}),
        },
      },
    })),
    setShelves: (shelves: Shelf[]) => setState(prev => ({
      ...prev,
      polygons: {
        ...prev.polygons,
        shelves: {
          entities: shelves.reduce<Record<string, Shelf>>((acc, s) => { acc[s.id] = s; return acc; }, {}),
        },
      },
    })),
    setTables: (tables: Table[]) => setState(prev => ({
      ...prev,
      polygons: {
        ...prev.polygons,
        tables: {
          entities: tables.reduce<Record<string, Table>>((acc, t) => { acc[t.id] = t; return acc; }, {}),
        },
      },
    })),
    setCheckoutZones: (zones: CheckoutZoneData[]) => setState(prev => ({
      ...prev,
      polygons: {
        ...prev.polygons,
        checkoutZones: {
          entities: zones.reduce<Record<string, CheckoutZoneData>>((acc, z) => { acc[z.id] = z; return acc; }, {}),
        },
      },
    })),
    setMode: (mode: CanvasMode) => setState(prev => ({
      ...prev,
      polygons: { ...prev.polygons, ui: { ...prev.polygons.ui, mode } },
    })),
    setSelectedPolygon: (polygonId: string | null, type: PolygonEntityType | null) => setState(prev => ({
      ...prev,
      polygons: {
        ...prev.polygons,
        ui: {
          ...prev.polygons.ui,
          selectedPolygon: polygonId,
          selectedPolygonType: type,
        },
      },
    })),
    setFrameLoading: (loading: boolean) => setState(prev => ({ ...prev, frameLoading: loading })),
    setZoneSummary: (summary: ZoneSummary) => setState(prev => ({ ...prev, zoneSummary: summary })),
    setZoneSummaryLoading: (loading: boolean) => setState(prev => ({ ...prev, zoneSummaryLoading: loading })),
    requestEdit: (entityId: string, entityType: PolygonEntityType, options?: { openDetails?: boolean }) => setState(prev => ({ ...prev, pendingEdit: { entityId, entityType, openDetails: options?.openDetails } })),
    clearPendingEdit: () => setState(prev => ({ ...prev, pendingEdit: null })),
  }), []);

  return (
    <StateContext.Provider value={state}>
      <ActionsContext.Provider value={actions}>{children}</ActionsContext.Provider>
    </StateContext.Provider>
  );
};

export const useAppState = () => {
  const ctx = useContext(StateContext);
  if (!ctx) throw new Error('useAppState must be used within StoreProvider');
  return ctx;
};

export const useAppActions = () => {
  const ctx = useContext(ActionsContext);
  if (!ctx) throw new Error('useAppActions must be used within StoreProvider');
  return ctx;
};
